import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { PktrAppMenuService } from '@prospektr/pktr-app-menu';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AuthorizeService } from 'src/app/_core/access-control/authorize.service';
import { Campaign } from 'src/app/_shared/interface/campaign.interface';
import { CampaignService } from 'src/app/_shared/service/campaign.service';
import { CampaignState } from 'src/app/_shared/state/campaign.state';
import { UserState } from 'src/app/_shared/state/user.state';
import { AppState } from 'src/app/app.state';
import { ClientDashboardService } from 'src/app/client-dashboard/service/client-dashboard.service';
import { SubscriptionPlanService } from 'src/app/shared/service/subscription-plan.service';
import { ValidatorsService } from 'src/app/shared/service/validators.service';
import { default as constants } from 'src/constant.json';
import { CampaignAutomationDialogComponent } from '../campaign-automation-dialog/campaign-automation-dialog.component';

@Component({
  selector: 'app-campaigns',
  templateUrl: './campaigns.component.html',
  styleUrls: ['./campaigns.component.scss'],
})
export class CampaignsComponent implements OnInit, OnDestroy {
  @ViewChild('editCampaignDialog', { static: true }) editCampaignDialog: any;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  agentUserFetchParams = { userType: 'agent', limit: 20, offset: 0, searchQuery: null };
  loanOfficerUserFetchParams = { userType: 'loanOfficer', limit: 20, offset: 0, searchQuery: null };
  isOpenInfo = false;
  isOpenCampaignInfo: boolean;
  isOpenCampaignInformation: boolean = false;
  tableColumnList: string[] = [
    'campaignName',
    'candidateCount',
    'emailOpenRate',
    'userOnboardRate',
    'createdDate',
    'launchDate',
    'updatedDate',
    'action',
  ];
  totalRecordCount: 0;
  tableDataSource: MatTableDataSource<Campaign>;
  campaignNewName: string = '';
  activeCampaignId;
  selectedInfo: any;
  userPlan: any;
  canViewAgentMarketing: boolean = false;
  userRole: string;
  onDestroyNotifier$ = new Subject();
  defaultCampaignSelected = 'CAMP_IR_INVT';
  isAutomatedInfo: Boolean;
  selectedCampaignType: string = 'self';
  pageSize: any;
  pageIndex: number;
  campaignListFetchParams = { limit: 10, page: 0 };
  campPageIndex = 0;
  campPageSize = 10;
  isValueTest = false;
  campaignDataList: any;
  disableNext: boolean = false;

  constructor(
    public appState: AppState,
    private campaignService: CampaignService,
    public campaignState: CampaignState,
    public matDialog: MatDialog,
    private clientDashboardService: ClientDashboardService,
    private router: Router,
    private snackBar: MatSnackBar,
    private userState: UserState,
    public authorizeService: AuthorizeService,
    private subscriptionService: SubscriptionPlanService,
    public _pktrAppMenuService: PktrAppMenuService,
    public breakpointService: ValidatorsService
  ) { }

  ngOnInit() {
    this.campaignState.isAutomatedInfo$.subscribe((value) => {
      this.isAutomatedInfo = value;
    });

    setTimeout(() => {
      this.canViewAgentMarketing = this.authorizeService.canView('agentMarketing');
    }, 2000);
    this.userState.userDetail$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res) {
        if (res?.roles?.length > 0) {
          this.userRole = res.roles[0];
        }
      }
    });

    this.userState.userPlanStatus.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res) {
        this.userPlan = res;
      }
    });

    if (this.appState?.appCompanyAliasValue == 'valuetest') {
      this.isValueTest = true;
      this.tableColumnList = [
        'campaignName',
        'campaignType',
        'candidateCount',
        'emailOpenRate',
        'createdDate',
        'launchDate',
        'updatedDate',
        'action',
      ];
    }

    this.campaignService.fetchCampaigns({
      page: this.campPageIndex,
      limit: this.campPageSize,
    });

    this.campaignState.campaignList$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res) {
        this.campaignDataList = res;
        this.disableNext = false;
      } else {
        this.campaignDataList = [];
      }

      this.tableDataSource = new MatTableDataSource(res?.data || []);
      this.totalRecordCount = res?.totalRecordCount || 0;
      if (!res) {
        this.disableNext = true;
      }
    });

    this.fetchActivePartners();
  }

  pageChange(event: PageEvent) {
    if (event.pageSize !== this.campaignListFetchParams.limit) {
      this.paginator.pageIndex = 0;
    }
    this.campaignListFetchParams.limit = event.pageSize;
    this.campaignListFetchParams.page = event.pageIndex;
    this.campaignService.fetchCampaigns(this.campaignListFetchParams);
  }

  ngOnDestroy() {
    this.onDestroyNotifier$?.next();
    this.onDestroyNotifier$?.complete();
  }

  onBooleanChanged(value) {
    this.isOpenCampaignInformation = value;
  }

  launchCampaign(campaign) {
    const sendImmediately = true;
    const scheduledCampTypeList = [
      constants.CAMPAIGN_TYPE.myHome,
      constants.CAMPAIGN_TYPE.marketValueCamp,
      constants.CAMPAIGN_TYPE.valueUpdateCamp,
      constants.CAMPAIGN_TYPE.cashOutRefinanceCamp,
    ];

    if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.campaignAccess.key)) return;

    if (this.appState.appCompanyAliasValue == 'valuetest') {
      // if (
      //   this.userState.userDetails?.roles.includes('loanOfficer') &&
      //   !this.userPlan?.featureList?.campaignAccess?.enabled
      // ) {
      //   this.openSnackbar(
      //     'The option to create/launch campaign is only available with CompCheck Premier Plan. Please upgrade your plan to access this feature.',
      //     'snackbar-warning'
      //   );
      //   return;
      // }

      // if (this.userPlan?.planType != 'subscription' && !this.userState.userDetails?.roles.includes('companyManager')) {
      //   this.openSnackbar(
      //     'The option to create/launch campaign is only available with our monthly subscription plan. Please upgrade your plan to access this feature.',
      //     'snackbar-warning'
      //   );
      // } else {
      const campaignAutomationDialogRef = this.matDialog.open(CampaignAutomationDialogComponent);
      campaignAutomationDialogRef
        .afterClosed()
        .pipe(take(1), takeUntil(this.onDestroyNotifier$))
        .subscribe((frequency) => {
          if (frequency)
            this.campaignService
              .launchCampaign(campaign._id, frequency, sendImmediately)
              .pipe(takeUntil(this.onDestroyNotifier$))
              .subscribe();
        });
      // }
    } else if (scheduledCampTypeList.includes(campaign.type)) {
      this.campaignState.selectedCampValue = campaign.type;
      const campaignAutomationDialogRef = this.matDialog.open(CampaignAutomationDialogComponent);
      campaignAutomationDialogRef
        .afterClosed()
        .pipe(take(1), takeUntil(this.onDestroyNotifier$))
        .subscribe((frequency) => {
          if (frequency)
            this.campaignService
              .launchCampaign(campaign._id, frequency, sendImmediately)
              .pipe(takeUntil(this.onDestroyNotifier$))
              .subscribe();
        });
    } else {
      this.campaignService
        .launchCampaign(campaign._id, null, null)
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe();
    }
  }

  deleteCampaign(id) {
    this.campaignService.openDeleteCampaignDialog(id);
  }

  openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 3000,
        panelClass: [style ? style : ''],
        verticalPosition: 'top',
      });
    }
  }

  openCampaignSheet(campaign?) {
    this.breakpointService
      .isMobileOrTablet()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((isMobileOrTablet) => {
        if (isMobileOrTablet) {
          this._pktrAppMenuService.close(true);
        }

      });
    if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.campaignAccess.key)) return;

    if (
      this.userState.userDetails?.roles.includes('companyManager') ||
      this.userState.userDetails?.roles.includes('loanOfficer') ||
      this.userState.userDetails?.roles.includes('companyEmployee')
    ) {
      this.fetchNextAgentUserList();
    }
    if (
      this.userState.userDetails?.roles.includes('companyManager') ||
      this.userState.userDetails?.roles.includes('agent') ||
      this.userState.userDetails?.roles.includes('companyEmployee')
    )
      this.fetchNextLoanOfficerUserList();
    this.campaignState.campaignToEditValue = campaign || null;
    this.campaignState.openCreateCampaignSheetValue = true;
    // }
  }

  renameCampaign(id, campaignNewName) {
    let campaign = { name: campaignNewName };
    this.campaignService.updateCampaign(id, campaign).pipe(takeUntil(this.onDestroyNotifier$)).subscribe();
    this.matDialog.closeAll();
    this.campaignNewName = '';
  }

  openSnackbar(message, style) {
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [style],
      verticalPosition: 'top',
    });
  }

  fetchActivePartners() {
    this.clientDashboardService
      .fetchActivePartners()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res: any) => { });
  }
  fetchNextAgentUserList() {
    this.clientDashboardService
      .fetchAgents(this.agentUserFetchParams)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res: any) => { });
  }

  fetchNextLoanOfficerUserList() {
    this.clientDashboardService
      .fetchLoanOfficers(this.loanOfficerUserFetchParams)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res: any) => { });
  }

  accountUpgrade() {
    this.router.navigate(['/subscription/account-upgrade']);
  }
  openInfo(value) {
    this.selectedInfo = value;
    this.isOpenInfo = true;
  }
  onBack(event) {
    this.selectedInfo = null;
    this.campaignService.fetchCampaigns();
    this.isOpenInfo = event;
  }

  nextPage() {
    this.campPageIndex++;
    this.campaignService.fetchCampaigns({
      page: this.campPageIndex,
      limit: this.campPageSize,
    });

    this.campaignState.campaignList$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res) {
        this.campaignDataList = res;
      }
      if (this.campaignDataList?.length == 0 && this.campPageIndex > 0) {
        this.campaignState.isLoadingCampaignListValue = true;
        this.campPageIndex--;
        this.openSnackBar('No new campaigns available', 'snackbar-error');
        setTimeout(() => {
          this.campaignService.fetchCampaigns({
            page: this.campPageIndex,
            limit: this.campPageSize,
          });
        }, 0);
        this.disableNext = true;
        this.campaignState.isLoadingCampaignListValue = false;
      }
    });
  }

  previousPage() {
    if (this.campPageIndex === 0) return;
    this.campPageIndex--;

    this.campaignService.fetchCampaigns({
      page: this.campPageIndex,
      limit: this.campPageSize,
    });
  }
}
